/* eslint-disable @typescript-eslint/naming-convention */
import React, { createContext, useContext, PropsWithChildren, useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { createSlug } from 'helpers'
import { ItemType } from 'widgets/src/components/Item'
import { getValue } from 'firebase-utils'
import { IconNameType } from 'foundations-library/types'
// import { shuffleArray } from 'helpers'

const checkIfRouteMatches = (pathname, str) => {
    // todo find a better way to do this, it is super messy

    const cleanedPathName = decodeURIComponent(pathname).replace(/ /g, '').toLowerCase()
    const cleanedStr = decodeURIComponent(str).replace(/ /g, '').toLowerCase()

    const pathArray = cleanedPathName.split('/')
    const strArray = cleanedStr.split('/')

    let isMatch = true
    // loop through path and check if it matches the string skipping * values
    if (pathArray.length !== strArray.length) isMatch = false
    pathArray.forEach((x, i) => {
        if (strArray[i] !== '*' && x !== strArray[i]) {
            isMatch = false
        }
    })

    return isMatch
}

const reorderArticles = (articles, pathname) => {
    if (!pathname || !articles?.length) return []
    const preferred = []
    const defaultArticles = []
    for (const article of articles) {
        if (article?.preferredRoutes?.find((preferredRoute) => checkIfRouteMatches(pathname, preferredRoute))) {
            preferred.push(article)
        } else {
            defaultArticles.push(article)
        }
    }
    // if (preferred.length) preferred = shuffleArray(preferred)
    // if (defaultArticles.length) defaultArticles = shuffleArray(defaultArticles)

    return [...preferred, ...defaultArticles]
}

const formatSectionData = (data, pathname = ''): WidgetSectionDataType => {
    const articles = pathname ? reorderArticles(data.articles, pathname) : (data.articles || [])
    const defaultSectionName = 'Articles'
    const defaultData = {
        sectionName: defaultSectionName,
        viewAllButtonText: 'View all articles',
        slug: data?.slug || data.directRoute || createSlug(data?.sectionName || defaultSectionName),
        maxWidth: '1050px',
        icon: 'cellular',
        articlesTitle: 'Top How-to-articles',
        selectedArticleCategory: null
    }
    const newData = {
        ...defaultData,
        ...data,
        articles
    }
    newData.articles = newData.articles.map((x) => {
        x.slug = x.slug || x.directRoute || createSlug(x.title)
        return x
    })

    return newData
}
/**
 *
 */
export type WidgetArticleType = ItemType
/**
 *
 */
export type WidgetSectionDataType = {
    articles: WidgetArticleType[],
    sectionName: string,
    viewAllButtonText: string,
    maxWidth?: string,
    icon?: IconNameType,
    articlesTitle?: string,
    directRoute: string;
    slug: string;
    category?: string;
    isModal?: boolean;
    selectedArticleCategory?: string | null;
}

type WidgetContextDataType = {
    pageBasedData: WidgetSectionDataType,
    findArticleBySlugs(sectionSlug: string, articleSlug: string): WidgetArticleType | null,
    remoteValue: { sections: WidgetSectionDataType[] },
    getArticlesBySectionSlug(v: string): WidgetSectionDataType
    findSectionBySlug(v): WidgetSectionDataType,
    findArticlesBySectionAndCategory(sectionSlug: string, articleCategory: string): WidgetArticleType[] | null
    setPageBasedData(sectionData: WidgetSectionDataType, pathname?: string): void
    setSelectedSection(selectedSection: string): void,
    selectedSection: string,
    isBottomWidgetVisible: boolean,
    hideBottomWidget: () => void,
    showBottomWidget: () => void
}

const WidgetContext = createContext<WidgetContextDataType | undefined>(undefined)

const emptyValues = {
    articles: [],
    sectionName: '',
    viewAllButtonText: '',
    slug: '',
    selectedArticleCategory: null
}

/**
 *
 */
const WidgetProvider = ({ children }: PropsWithChildren<any>): JSX.Element => {
    const location = useLocation()
    const [pageBasedData, _setPageBasedData] = useState({ ...emptyValues })
    const [selectedSection, _setSelectedSection] = useState('')
    const [isBottomWidgetVisible, setIsBottomWidgetVisible] = useState(true)

    const remoteValue = useMemo((): { sections: WidgetSectionDataType[] } => {
        const widgets = JSON.parse(getValue('widgets') || '{}')
        const widgetsSms = JSON.parse(getValue('widgets_sms') || '{}')

        const data = {
            ...widgets,
            sections: [
                ...(widgets.sections ?? []),
                ...(widgetsSms.sections ?? [])
            ]
        }
        data.sections = data.sections.map((x) => formatSectionData(x))

        return data
    }, [])

    const getArticlesBySectionSlug = (slug) => {
        const section = remoteValue.sections.find((x) => x.slug === slug)
        if (!section) return null
        return formatSectionData(section)
    }

    const setPageBasedData = (sectionData, pathname = '') => {
        _setPageBasedData(formatSectionData(sectionData, pathname))
    }

    const setSelectedSection = (selectedSection = '') => {
        _setSelectedSection(selectedSection)
    }

    const hideBottomWidget = () => {
        setIsBottomWidgetVisible(false)
    }

    const showBottomWidget = () => {
        setIsBottomWidgetVisible(true)
    }

    useEffect(() => {
        let section
        const locationWithHash = location.pathname + location.hash
        if (!checkIfRouteMatches(location.pathname, '/support-center')) {
            section = remoteValue.sections.find(
                (x) => x.routes.find((y) => checkIfRouteMatches(locationWithHash, y))
            )
        }

        if (!section) {
            setPageBasedData({ ...emptyValues })
        } else {
            setPageBasedData(section, locationWithHash)
        }
    }, [remoteValue, location])

    const findSectionBySlug = (slug) => remoteValue.sections.find((x) => x.slug === slug)

    const findArticleBySlugs = (sectionSlug, articleSlug): WidgetArticleType | null => {
        const section = findSectionBySlug(sectionSlug)
        if (!section) return null

        const article = section.articles.find((x) => x.slug === articleSlug)
        return article || null
    }

    const findArticlesBySectionAndCategory = (sectionSlug, articleCategory): WidgetArticleType[] | null => {
        const section = findSectionBySlug(sectionSlug)
        if (!section) return null

        const articles = section.articles?.filter((x) => x.category === articleCategory) ?? []
        return articles
    }

    return (
        <WidgetContext.Provider value={{
            pageBasedData,
            setPageBasedData,
            findArticleBySlugs,
            findArticlesBySectionAndCategory,
            remoteValue,
            getArticlesBySectionSlug,
            findSectionBySlug,
            setSelectedSection,
            selectedSection,
            isBottomWidgetVisible,
            hideBottomWidget,
            showBottomWidget
        }}>
            {children}
        </WidgetContext.Provider>
    )
}

/**
 *
 */
const useWidgetContext = (): WidgetContextDataType => {
    const widgetData = useContext(WidgetContext)
    if (widgetData === undefined) {
        throw new Error('useWidgetContext must be used inside WidgetProvider tags')
    }

    return { ...widgetData }
}

/**
 *
 */
export { WidgetContext, WidgetProvider, useWidgetContext, WidgetContextDataType }
